import {AxiosError} from "axios";
import {useState} from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import InputGroup from 'react-bootstrap/InputGroup';
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import {useTranslation} from "react-i18next";
import {useNavigate, useSearchParams} from "react-router-dom";
import {ApiConfig} from "../api/portal/ApiConfig";
import {PDFApi} from "../api/portal/generated";
import {CopyToClipboard} from "../common/CopyToClipboardButton";
import {RenderAlert} from "../common/RenderAlert";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";
import {PDFAuthenticatorSetupWizard} from "./PDFAuthenticatorSetupWizard";

/*
 * REST api for accessing the back-end
 */
const pdfApi = new PDFApi(ApiConfig.config());

export const PDFGeneratePassword = () =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * searchParams is used to get the code from the URL
     */
    const [searchParams] = useSearchParams();
    /**
     * The base64 encoded JSON containing the OTP parameters
     */
    const [otpCode, setOtpCode] = useState<string>(searchParams.get("d")??"");
    /**
     * The generated password
     */
    const [password, setPassword] = useState<string>();
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while generating the password
     */
    const [generating, setGenerating] = useState<boolean>(false);
    /**
     * Set to true when a value is copied to the clipboard
     */
    const [copied, setCopied] = useState<boolean>(false);
    /**
     * If true, the "CipherMail PDF Authenticator" wizard will be shown
     */
    const [showSetupWizard, setShowSetupWizard] = useState<boolean>(false);
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    const generatePassword = () =>
    {
        logger.debug("Getting password")

        if (otpCode === undefined) {
            throw Error("otpCode is not set")
        }

        // reset state
        setCopied(false);
        setErrorMessage(undefined);
        setGenerating(true);

        pdfApi.pDFControllerGetPdfPassword(otpCode)
            .then((response) => {
                setPassword(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error generating password", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setGenerating(false);
            });
    }

    /**
     * Is called when the Setup Wizard is closed
     */
    const onPDFAuthenticatorSetupWizardClose = () => {
        setShowSetupWizard(false);
    }

    return (
        <>
            <Row>
                <Form className="mt-3">
                    {/* show an alert if a validation error occurred */}
                    <RenderAlert message={errorMessage}/>

                    {copied && <Alert className="text-break" variant={"success"}>{t('common.copied-to-clipboard')}</Alert>}

                    {showSetupWizard && <PDFAuthenticatorSetupWizard onClose={onPDFAuthenticatorSetupWizardClose} />}

                    <Col lg={6} md={12}>
                        <FloatingLabel label={t('pdf-generate-password.otp-label')} className="text-secondary mb-3">
                            <Form.Control type="text" value={otpCode} placeholder="OTP code"
                                          onChange={e => setOtpCode(e.target.value)}/>
                        </FloatingLabel>

                        {(otpCode === null || otpCode.length === 0) ? <div className="m-3">
                            {t('pdf-generate-password.if-no-otpid')}
                        </div> : null}

                        <InputGroup className="mb-3 mt-5">
                            <FloatingLabel label={t('pdf-generate-password.password-label')} className="text-secondary mb-3">
                                <Form.Control type="text" value={password} placeholder="Password" disabled={true} readOnly={true}/>
                            </FloatingLabel>
                            <InputGroup.Text className="border-0 d-inline">
                                <CopyToClipboard value={password} setCopied={setCopied}/>
                            </InputGroup.Text>
                        </InputGroup>
                    </Col>
                </Form>
            </Row>
            <Row>
                <div className="col pt-3 pb-3 border-top border-bottom">
                    <Button
                        variant="success"
                        type="submit"
                        onClick={() => generatePassword()}
                        disabled={StringUtils.isBlank(otpCode)}
                    >
                        {generating && <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            aria-hidden="true"
                            className="me-1"
                        />}
                        {generating ? t('pdf-generate-password.generating') : t('pdf-generate-password.generate')}
                    </Button>
                    <Button
                        className="ms-3"
                        variant="primary"
                        onClick={() => setShowSetupWizard(true)}
                    >
                        {t('pdf-generate-password.setup-pdf-authenticator-button')}
                    </Button>
                </div>
            </Row>
        </>
    )
}
